import { addMinutes } from "date-fns";
import React, { FunctionComponent, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useCheckNationalStockLevelByPartId } from "../../../../queries/useCheckNationalStockLevelByPartId";
import { createQueryStringHook } from "../../../../stores/useQueryState";
import {
  DateDisplay,
  TIME_FORMAT,
} from "../../../../ui/DateDisplay/DateDisplay";
import { Dialog } from "../../../../ui/Dialog/Dialog";
import { DialogHeader } from "../../../../ui/Dialog/DialogHeader";
import { LinearProgress } from "../../../../ui/LinearProgress";
import { ProvinceSelect, useProvince } from "./ProvinceSelect";

export const useStockLevelsModal = createQueryStringHook(
  "stock-levels-part-id",
  ""
);

// const useCurrentDate = (disabled: boolean): Date => {
//   const [date, setDate] = useState(new Date());
//   const [offsetSeconds, setOffsetSeconds] = useState(0);
//   useEffect(() => {
//     setDate(new Date());
//     setOffsetSeconds(0);
//     let intervalId: NodeJS.Timeout;
//     if (!disabled) {
//       intervalId = setInterval(() => {
//         setOffsetSeconds((seconds) => seconds + 1);
//       }, 1000);
//     }
//     return () => {
//       clearInterval(intervalId);
//     };
//   }, [disabled]);
//   return addSeconds(date, offsetSeconds);
// };

const Component: FunctionComponent = () => {
  const [partId, setPartId] = useStockLevelsModal();
  const hide = (): void => setPartId("");
  const { t } = useTranslation();
  const [province] = useProvince();

  const { data, isLoading } = useCheckNationalStockLevelByPartId(partId, {
    keepPreviousData: true,
    refreshInterval: 1000,
  });
  const now = Date.now();
  const provinceSet = useMemo(() => {
    const resultingSet = new Set<string>();
    data?.nationalStockTable.nationalStockList.forEach(({ locationState }) => {
      resultingSet.add(locationState);
    });
    return resultingSet;
  }, [data]);

  return (
    <Dialog
      className="xl:w-full xl:max-w-7xl"
      id="stock-levels-modal"
      onClose={hide}
      placement="top"
      visible={Boolean(partId)}
    >
      <DialogHeader
        title={
          <span className="grid gap-2 sm:flex sm:items-center sm:gap-4">
            <span>{t("searchResults.nationalStock")}:</span>
            <ProvinceSelect availableProvinces={provinceSet} />
          </span>
        }
        onClose={hide}
      />
      <section className="p-4">
        {isLoading && <LinearProgress className="h-0.5" />}
        {Boolean(data?.nationalStockTable.nationalStockList.length) && (
          <>
            <div className=" overflow-x-auto">
              <table className="border-collapse table-auto w-full text-xs text-ink-2">
                <thead>
                  <tr className="border-b-2 align-bottom text-left">
                    <th className="pr-2">
                      {t("stockLevelsModal.distributor")}
                    </th>
                    <th className="pr-2">{t("stockLevelsModal.location")}</th>
                    <th className="pr-2">{t("stockLevelsModal.province")}</th>
                    <th className="pr-2">{t("stockLevelsModal.city")}</th>
                    <th className="pr-2">{t("stockLevelsModal.phone")}</th>
                    <th className="pr-2">{t("stockLevelsModal.qty")}</th>
                    <th className="">{t("stockLevelsModal.lastUpdated")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.nationalStockTable.nationalStockList
                    .filter(({ locationState }) =>
                      province ? locationState === province : true
                    )
                    .map(
                      (
                        {
                          locationCity,
                          locationDistributorName,
                          locationItemQty,
                          locationMinutesSinceLastStockUpdate,
                          locationName,
                          locationPhone,
                          locationState,
                        },
                        key
                      ) => {
                        const timeFromNow = addMinutes(
                          now,
                          -locationMinutesSinceLastStockUpdate
                        );
                        return (
                          <tr className="border-b align-top" key={key}>
                            <td className="pr-2">{locationDistributorName}</td>
                            <td className="pr-2">{locationName}</td>
                            <td className="pr-2">{locationState}</td>
                            <td className="pr-2">{locationCity}</td>
                            <td className="pr-2">{locationPhone}</td>
                            <td className="pr-2 text-right">
                              {locationItemQty}
                            </td>
                            <td className="">
                              <DateDisplay>{timeFromNow}</DateDisplay>
                              <br />
                              <DateDisplay format={TIME_FORMAT}>
                                {timeFromNow}
                              </DateDisplay>
                              {/* {format(timeFromNow, "hh:mm:ss a")} */}
                            </td>
                          </tr>
                        );
                      }
                    )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </Dialog>
  );
};

export const StockLevelsModal = memo(Component);
